import React, { useEffect, useState } from 'react';
import { useAppState } from '../src/contexts/AppStateContext';
import api from '../src/utils/axiosConfig';
import ConfirmModal from './ConfirmModal';
import { User, UserX, UserPlus, Mail, X, Edit, Check, MoreVertical } from 'lucide-react';

const TeamManagement = () => {
  const { currentOrganization } = useAppState();
  const [members, setMembers] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('admin');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [invitationToDelete, setInvitationToDelete] = useState(null);
  const [isRemoving, setIsRemoving] = useState(false);

  // Close the open dropdown when clicking outside of any row's menu.
  // Each row has its own menu, so a single ref can't identify "inside";
  // walk up from the click target instead.
  useEffect(() => {
    function handleClickOutside(event) {
      if (!event.target.closest('[data-member-menu]')) {
        setActiveDropdown(null);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch team members and pending invitations
  useEffect(() => {
    const fetchTeamData = async () => {
      if (!currentOrganization || !currentOrganization.id) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Fetch members
        const membersResponse = await api.get(`/organizations/${currentOrganization.id}/members/`);
        setMembers(membersResponse.data);
        
        // Fetch pending invitations
        const invitationsResponse = await api.get(`/organizations/${currentOrganization.id}/invitations/`);
        setPendingInvitations(invitationsResponse.data);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching team data:', error);
        setError('Failed to load team data. Please try again.');
        setLoading(false);
      }
    };
    
    fetchTeamData();
  }, [currentOrganization]);

  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    
    if (!inviteEmail.trim()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await api.post(`/organizations/${currentOrganization.id}/invitations/`, {
        email: inviteEmail,
        role: inviteRole
      });
      
      // Add the new invitation to the list
      setPendingInvitations([...pendingInvitations, response.data]);
      
      // Reset form
      setInviteEmail('');
      setInviteRole('admin');
      setShowInviteForm(false);
      
    } catch (error) {
      console.error('Error sending invitation:', error);
      setError('Failed to send invitation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteInvitation = async () => {
    if (!invitationToDelete) return;
    setIsRemoving(true);
    setError(null);

    try {
      await api.delete(`/organizations/${currentOrganization.id}/invitations/${invitationToDelete.id}/`);

      // Remove the invitation from the list
      setPendingInvitations(pendingInvitations.filter(inv => inv.id !== invitationToDelete.id));
      setInvitationToDelete(null);

    } catch (error) {
      console.error('Error deleting invitation:', error);
      setError('Failed to delete invitation. Please try again.');
      setInvitationToDelete(null);
    } finally {
      setIsRemoving(false);
    }
  };

  const handleRemoveMember = async () => {
    if (!memberToRemove) return;
    setIsRemoving(true);
    setError(null);

    try {
      await api.delete(`/organizations/${currentOrganization.id}/members/${memberToRemove.id}/`);

      // Remove the member from the list
      setMembers(members.filter(member => member.id !== memberToRemove.id));
      setMemberToRemove(null);

    } catch (error) {
      console.error('Error removing member:', error);
      setError('Failed to remove member. Please try again.');
      setMemberToRemove(null);
    } finally {
      setIsRemoving(false);
    }
  };

  const memberDisplayName = (member) =>
    [member.user.first_name, member.user.last_name].filter(Boolean).join(' ') || member.user.email;

  const handleUpdateRole = async (memberId, newRole) => {
    try {
      const response = await api.post(`/organizations/${currentOrganization.id}/members/${memberId}/`, {
        role: newRole
      });
      
      // Update the member in the list
      setMembers(members.map(member => 
        member.id === memberId ? { ...member, role: newRole } : member
      ));
      
      // Close edit form
      setEditingMember(null);
      
    } catch (error) {
      console.error('Error updating member role:', error);
      setError('Failed to update member role. Please try again.');
    }
  };

  const toggleDropdown = (id) => {
    setActiveDropdown(activeDropdown === id ? null : id);
  };

  const roleLabels = {
    'admin': 'Admin',
    'event_manager': 'Event Manager',
    'usher': 'Usher',
    'submissions_manager': 'Submissions Manager',
  };

  if (!currentOrganization) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">Please select an organization to view team members</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="flex justify-center">
          <svg className="animate-spin h-8 w-8 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        <p className="text-gray-500 mt-2">Loading team data...</p>
      </div>
    );
  }

  return (
    <div className="bg-white p-6">
      {error && (
        <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-md">
          <p>{error}</p>
        </div>
      )}
      
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-800">Team Members</h2>
          <button 
            onClick={() => setShowInviteForm(!showInviteForm)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center"
          >
            <UserPlus size={16} className="mr-2" />
            Invite Member
          </button>
        </div>
        
        {showInviteForm && (
          <div className="mb-6 p-4 bg-gray-50 rounded-md border border-gray-200">
            <h3 className="text-lg font-medium mb-3">Invite New Member</h3>
            <form onSubmit={handleInviteSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="col-span-2">
                  <label htmlFor="inviteEmail" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail size={16} className="text-gray-400" />
                    </div>
                    <input
                      type="email"
                      id="inviteEmail"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="email@example.com"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="inviteRole" className="block text-sm font-medium text-gray-700 mb-1">
                    Role
                  </label>
                  <select
                    id="inviteRole"
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                    className="block w-full py-2 px-3 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="admin">Admin</option>
                    <option value="event_manager">Event Manager</option>
                    <option value="usher">Usher</option>
                    <option value="submissions_manager">Submissions Manager</option>
                  </select>
                </div>
              </div>
              <div className="mt-4 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowInviteForm(false)}
                  className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                >
                  {isSubmitting ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </form>
          </div>
        )}
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Joined
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {members.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                    No team members found
                  </td>
                </tr>
              ) : (
                members.map((member) => (
                  <tr key={member.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                          {member.user.profile_image ? (
                            <img src={member.user.profile_image} alt="" className="h-10 w-10 rounded-full" />
                          ) : (
                            <User size={20} className="text-gray-500" />
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {member.user.first_name} {member.user.last_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            @{member.user.username || 'user'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{member.user.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingMember === member.id ? (
                        <div className="flex items-center space-x-2">
                          <select
                            value={member.role}
                            onChange={(e) => {
                              setMembers(members.map(m => 
                                m.id === member.id ? { ...m, role: e.target.value } : m
                              ));
                            }}
                            className="block w-full py-1 px-2 text-sm border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                          >
                            <option value="admin">Admin</option>
                            <option value="event_manager">Event Manager</option>
                            <option value="usher">Usher</option>
                            <option value="submissions_manager">Submissions Manager</option>
                          </select>
                          <button
                            onClick={() => handleUpdateRole(member.id, members.find(m => m.id === member.id).role)}
                            className="p-1 text-indigo-600 hover:text-indigo-900"
                          >
                            <Check size={16} />
                          </button>
                          <button
                            onClick={() => setEditingMember(null)}
                            className="p-1 text-gray-400 hover:text-gray-500"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                            ${member.role === 'owner' ? 'bg-purple-100 text-purple-800' : 
                              member.role === 'admin' ? 'bg-indigo-100 text-indigo-800' :
                              member.role === 'event_manager' ? 'bg-green-100 text-green-800' : 
                              member.role === 'submissions_manager' ? 'bg-amber-100 text-amber-800' : 
                              'bg-blue-100 text-blue-800'}`
                          }>
                            {roleLabels[member.role] || member.role}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(member.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="relative" data-member-menu>
                        <button
                          onClick={() => toggleDropdown(member.id)}
                          className="text-gray-400 hover:text-gray-500 focus:outline-none"
                        >
                          <MoreVertical size={16} />
                        </button>
                        
                        {activeDropdown === member.id && (
                          <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                            <div className="py-1" role="menu" aria-orientation="vertical">
                              <button
                                onClick={() => {
                                  setEditingMember(member.id);
                                  setActiveDropdown(null);
                                }}
                                className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              >
                                <Edit size={16} className="mr-3 text-gray-400" />
                                Edit Role
                              </button>
                              {member.role !== 'owner' && (
                                <button
                                  onClick={() => {
                                    setMemberToRemove(member);
                                    setActiveDropdown(null);
                                  }}
                                  className="flex w-full items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                                >
                                  <UserX size={16} className="mr-3 text-red-400" />
                                  Remove Member
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Pending Invitations Section */}
      <div className="mt-10">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Pending Invitations</h2>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sent Date
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pendingInvitations.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">
                    No pending invitations
                  </td>
                </tr>
              ) : (
                pendingInvitations.map((invitation) => (
                  <tr key={invitation.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                          <Mail size={16} className="text-gray-400" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {invitation.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${invitation.role === 'admin' ? 'bg-indigo-100 text-indigo-800' :
                          invitation.role === 'event_manager' ? 'bg-green-100 text-green-800' : 
                          invitation.role === 'submissions_manager' ? 'bg-amber-100 text-amber-800' : 
                          'bg-blue-100 text-blue-800'}`
                      }>
                        {roleLabels[invitation.role] || invitation.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(invitation.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => setInvitationToDelete(invitation)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <X size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmModal
        isOpen={memberToRemove !== null}
        title="Remove team member"
        message={
          memberToRemove
            ? `Remove ${memberDisplayName(memberToRemove)} from ${currentOrganization.name}? They will lose access to all of its events.`
            : ''
        }
        confirmLabel="Remove Member"
        onConfirm={handleRemoveMember}
        onCancel={() => setMemberToRemove(null)}
        isProcessing={isRemoving}
      />

      <ConfirmModal
        isOpen={invitationToDelete !== null}
        title="Delete invitation"
        message={
          invitationToDelete
            ? `Delete the pending invitation for ${invitationToDelete.email}? The invitation link will stop working.`
            : ''
        }
        confirmLabel="Delete Invitation"
        onConfirm={handleDeleteInvitation}
        onCancel={() => setInvitationToDelete(null)}
        isProcessing={isRemoving}
      />
    </div>
  );
};

export default TeamManagement;